import React, { useEffect, useState } from "react";
import { ITreatment } from "../types/clinic.types";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import baseUrl from "../../../../api/baseUrl";
import { toast } from "react-toastify";

interface Props {
  treatment: ITreatment | null;
  onClose: () => void;
  onSave: (treatment: ITreatment) => void;
}

interface CatalogRow {
  id: string;
  name: string;
  description: string;
}

type TreatmentFormData = Omit<ITreatment, "subTreatments" | "id"> & {
  id?: string;
};

function toFormData(t: ITreatment): TreatmentFormData {
  return {
    id: t.id,
    treatmentId: String(t.treatmentId ?? ""),
    name: t.name,
    description: t.description,
    localPrice: t.localPrice,
    foreginPrice: t.foreginPrice,
  };
}

function extractNewBookTreatmentId(
  responseData: { bookTreatment?: { _id?: string; treatmentId?: string | { _id?: string } }[] },
  catalogTreatmentId: string
): string | undefined {
  const rows = responseData?.bookTreatment ?? [];
  const match = rows.find((row) => {
    const tid = row.treatmentId;
    const tidStr =
      typeof tid === "object" && tid !== null && "_id" in tid
        ? String(tid._id)
        : String(tid ?? "");
    return tidStr === String(catalogTreatmentId);
  });
  return match?._id ? String(match._id) : rows[rows.length - 1]?._id?.toString();
}

export const TreatmentFormModal: React.FC<Props> = ({
  treatment,
  onClose,
  onSave,
}) => {
  const isEditMode = !!treatment;
  const [formData, setFormData] = useState<TreatmentFormData>(() =>
    treatment ? toFormData(treatment) : { treatmentId: "", localPrice: "", foreginPrice: "" }
  );
  const [rows, setRows] = useState<CatalogRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (treatment) {
      setFormData(toFormData(treatment));
    }
  }, [treatment]);

  useEffect(() => {
    baseUrl
      .get("/client/booking/findBookClinicTreatment")
      .then((response: { data?: { data?: { _id: string; name: string; decription?: string }[] } }) => {
        const formattedRows = (response.data?.data ?? []).map((item) => ({
          id: String(item._id),
          name: item.name,
          description: item.decription ?? "",
        }));
        setRows(formattedRows);
      })
      .catch(console.error);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
    }));
  };

  const validatePrices = (): string[] => {
    const errors: string[] = [];
    if (!formData.treatmentId) errors.push("Treatment is required.");
    if (
      formData.localPrice === "" ||
      isNaN(Number(formData.localPrice)) ||
      Number(formData.localPrice) < 0
    ) {
      errors.push("Local price must be a valid non-negative number.");
    }
    if (
      formData.foreginPrice === "" ||
      isNaN(Number(formData.foreginPrice)) ||
      Number(formData.foreginPrice) < 0
    ) {
      errors.push("Foreign price must be a valid non-negative number.");
    }
    return errors;
  };

  const buildSavedTreatment = (
    bookTreatmentId: string,
    catalogTreatmentId: string
  ): ITreatment => {
    const catalog = rows.find((row) => row.id === String(catalogTreatmentId));
    return {
      id: bookTreatmentId,
      treatmentId: String(catalogTreatmentId),
      name: catalog?.name ?? treatment?.name,
      description: catalog?.description ?? treatment?.description,
      localPrice: Number(formData.localPrice),
      foreginPrice: Number(formData.foreginPrice),
      subTreatments: treatment?.subTreatments ?? [],
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validatePrices();
    if (errors.length > 0) {
      toast.error(errors.join(" "));
      return;
    }

    const payload = {
      treatmentId: formData.treatmentId,
      localPrice: Number(formData.localPrice),
      foreginPrice: Number(formData.foreginPrice),
    };

    setIsSaving(true);

    if (isEditMode) {
      baseUrl
        .patch("/client/booking/updateBookTreatments", {
          ...payload,
          id: treatment!.id,
        })
        .then((response) => {
          if (response.data?.success) {
            onSave(
              buildSavedTreatment(treatment!.id, String(formData.treatmentId))
            );
            toast.success("Treatment updated successfully!");
            onClose();
          } else {
            toast.error(response.data?.message || "Failed to update treatment");
          }
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            toast.error("This treatment is already added to your clinic.");
          } else {
            console.error(error);
            toast.error(
              error?.response?.data?.message || "Failed to update treatment"
            );
          }
        })
        .finally(() => setIsSaving(false));
    } else {
      baseUrl
        .patch("/client/booking/createBookTreatments", payload)
        .then((response) => {
          if (response.data?.success) {
            const newId = extractNewBookTreatmentId(
              response.data?.data ?? {},
              String(formData.treatmentId)
            );
            if (!newId) {
              toast.error("Treatment saved but could not read the new id. Please refresh.");
              return;
            }
            onSave(buildSavedTreatment(newId, String(formData.treatmentId)));
            toast.success("Treatment added successfully!");
            onClose();
          } else {
            toast.error(response.data?.message || "Failed to add treatment");
          }
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            toast.error("This treatment is already added to your clinic.");
          } else {
            console.error(error);
            toast.error(
              error?.response?.data?.message || "Failed to add treatment"
            );
          }
        })
        .finally(() => setIsSaving(false));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000] p-4">
      <div
        className="bg-white rounded-[8px] shadow-2xl max-w-[500px] w-full p-3 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-800 mb-0">
              {isEditMode ? "Edit Treatment" : "Add New Treatment"}
            </h3>
            <button
              type="button"
              className="p-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-full transition-colors"
              onClick={onClose}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Treatment Name
              </label>
              <select
                name="treatmentId"
                value={formData.treatmentId ?? ""}
                onChange={handleChange}
                required
                disabled={isEditMode}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700 disabled:bg-slate-50 disabled:text-slate-500"
              >
                <option value="">Select treatment</option>
                {rows.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                    {row.description ? ` - ${row.description}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Consulting Normal Price LKR
              </label>
              <input
                type="number"
                name="localPrice"
                value={formData.localPrice ?? ""}
                onChange={handleChange}
                placeholder="Enter price in LKR"
                required
                min={0}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Consulting Price for Foreigners USD
              </label>
              <input
                type="number"
                name="foreginPrice"
                value={formData.foreginPrice ?? ""}
                onChange={handleChange}
                placeholder="Enter price in USD"
                required
                min={0}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors text-xs"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs disabled:opacity-60"
            >
              <SaveAltIcon sx={{ fontSize: 16 }} />
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
